import React, { ReactElement, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/node';
import Page from '../components/Page';
import App, { AppContext, AppProps, NextWebVitalsMetric } from 'next/app';
import { ApolloProvider } from '@apollo/client';
import Router, { useRouter } from 'next/router';
import { PageProvider } from '@lib/pageContext';
import { ServicesProvider, AnalyticsOverviewProvider } from '@lib/servicesContext';
import { NextPageContext } from 'next/dist/shared/lib/utils';
import { useApollo } from '@lib/useApollo';
import { appWithTranslation, i18n } from 'next-i18next';

import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';
import { Metrics } from '@lib/metrics';
import { apiConfig } from '@lib/apiConfig';
import getConfig from 'next/config';
import NProgress from 'nprogress';
import 'emoji-mart/css/emoji-mart.css';
import '@lifechurch/chop-ui/styles';
import { AuthProvider } from '@lib/authContext';
import { ProtectRoute } from '@components/ProtectRoute';
import { ThemeProvider } from '@lifechurch/react-ion';
import { DEFAULT_LOCALE } from '@utils/constants';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps } from 'next';
import { AmplitudeProvider } from '@lib/amplitude/provider';
import { fetchAmplitudeExperimentsForRequest } from '@lib/amplitude/server';

Sentry.init({
  environment: getConfig().publicRuntimeConfig.ENV,
  dsn: getConfig().publicRuntimeConfig.SENTRY_DSN,
  release: getConfig().publicRuntimeConfig.BUILD_ID,
  enabled: getConfig().publicRuntimeConfig.ENV !== 'development',
});
Sentry.setExtra('ssr', typeof window === 'undefined');

type ErrType = Pick<NextPageContext, 'err'>;

interface Props extends AppProps<{ err?: ErrType; backgroundColor?: string }> {
  err: ErrType;
  apiConfig: ReturnType<typeof apiConfig>;
  amplitudeExperiments?: Record<string, any>;
}

function MyApp({
  amplitudeExperiments,
  apiConfig,
  Component,
  err,
  pageProps,
}: Props): ReactElement {
  const apollo = useApollo(apiConfig);
  const pageRef = useRef<HTMLDivElement>(null);
  const { locale } = useRouter();
  const dir = i18n?.dir(locale);

  useEffect(() => {
    document.documentElement.dir = dir || 'ltr';
  }, [dir]);

  useEffect(() => {
    if (!Cookies.get('SESSION_ID')) {
      Cookies.set('SESSION_ID', uuid());
    }

    Router.events.on('routeChangeStart', () => {
      NProgress.start();
      sessionStorage.setItem('REFERRER', window.location.href);
    });

    Router.events.on('routeChangeComplete', () => {
      NProgress.done();
      // Client side route, track pageview and restart heartbeat
      Metrics.pageView();
      Metrics.startHeartbeat();
    });

    // Initial render pageview
    Metrics.pageView();
    Metrics.startHeartbeat();
  }, []);

  return (
    <ThemeProvider openNetwork>
      <ApolloProvider client={apollo}>
        <AuthProvider>
          <AmplitudeProvider experiments={amplitudeExperiments || {}}>
            <ServicesProvider>
              <AnalyticsOverviewProvider>
                <PageProvider apiConfig={apiConfig} pageRef={pageRef}>
                  <ProtectRoute>
                    <Page backgroundColor={pageProps?.backgroundColor} pageRef={pageRef}>
                      <Component err={err} {...pageProps} />
                    </Page>
                  </ProtectRoute>
                </PageProvider>
              </AnalyticsOverviewProvider>
            </ServicesProvider>
          </AmplitudeProvider>
        </AuthProvider>
      </ApolloProvider>
    </ThemeProvider>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const request = appContext?.ctx.req;
  const config = apiConfig(request?.headers);
  const appProps = await App.getInitialProps(appContext);
  const amplitudeExperiments = await fetchAmplitudeExperimentsForRequest(request);

  return {
    ...appProps,
    apiConfig: config,
    amplitudeExperiments,
  };
};

export const getServerSideProps: GetServerSideProps = async ctx => ({
  props: {
    ...(await serverSideTranslations(ctx.locale || DEFAULT_LOCALE, ['common'])),
  },
});

export function reportWebVitals(metric: NextWebVitalsMetric): void {
  Metrics.webVital(metric);
}

// @ts-expect-error
export default appWithTranslation(MyApp);
